import React, { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import { AsYouType, isValidPhoneNumber } from 'libphonenumber-js';
import { COUNTRY_OPTIONS, splitPhoneNumber } from './Countrydata';
import { isFilled } from './FormField';
import './PhoneField.css';

// react-select is only used for ITS dropdown behavior (search, keyboard nav,
// portal-rendered menu). Every visual trace of it being "a separate control"
// is stripped out here so it reads as the left segment of one field.
const countrySelectStyles = {
  container: (base) => ({ ...base, height: '100%' }),
  control: (base) => ({
    ...base,
    height: '100%',
    minHeight: 0,
    border: 'none',
    boxShadow: 'none',
    background: 'transparent',
  }),
  valueContainer: (base) => ({ ...base, padding: '0 4px 0 14px' }),
  input: (base) => ({ ...base, margin: 0, padding: 0 }),
  indicatorsContainer: (base) => ({ ...base, paddingRight: 4 }),
  indicatorSeparator: () => ({ display: 'none' }),
  singleValue: (base) => ({ ...base, fontSize: 13, fontWeight: 600 }),
  menu: (base) => ({ ...base, zIndex: 99999, minWidth: 260 }),
  menuPortal: (base) => ({ ...base, zIndex: 99999 }),
};

const toOption = (country) => ({
  value: country.code,
  label: `${country.flag}  ${country.dialCode}`,
  country,
});

const countryOptions = COUNTRY_OPTIONS.map(toOption);

// Wrapped so a malformed/partial number (e.g. just "+" or "+9") can never
// throw mid-keystroke and crash the field - it just reads as "not valid yet".
const safeIsValidPhoneNumber = (fullNumber, countryCode) => {
  try {
    return isValidPhoneNumber(fullNumber, countryCode);
  } catch (error) {
    return false;
  }
};

/**
 * One seamless phone field: [country select | divider | number input],
 * wrapped in a single .pf-phone container that owns the border/radius/
 * shadow/readonly-bg - matching every other field on the screen exactly.
 *
 * Validation and as-you-type formatting come from libphonenumber-js, so
 * it's correct per-country rather than a flat digit-count guess.
 *
 * `onValidityChange(isValid)` lets a parent form gate its Save action on
 * this field without duplicating the validation rule itself.
 *
 * `forceShowError` lets a parent form reveal the error message on a failed
 * submit attempt even if the user never blurred the field (untouched).
 */
function PhoneField({
  id,
  label,
  country,
  localNumber,
  disabled,
  onCountryChange,
  onNumberChange,
  onValidityChange,
  forceShowError = false,
}) {
  const [touched, setTouched] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const selectedOption = useMemo(() => toOption(country), [country]);
  const fullNumber = `${country.dialCode}${localNumber}`;
  const isValid =
    !localNumber || safeIsValidPhoneNumber(fullNumber, country.code);
  const showError = (touched || forceShowError) && !disabled && !isValid;

  // Report validity up whenever it changes, so the parent form can decide
  // whether Save is allowed - without re-implementing the validation rule.
  useEffect(() => {
    if (onValidityChange) {
      onValidityChange(isValid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isValid]);

  const handleNumberChange = (e) => {
    const rawValue = e.target.value;
    const normalizedRawValue = String(rawValue || '').trim();

    if (normalizedRawValue.startsWith('+')) {
      const parsed = splitPhoneNumber(normalizedRawValue, country);
      onNumberChange(parsed.localNumber, {
        detectedCountry: parsed.country,
        rawValue: normalizedRawValue,
        isInternationalInput: true,
      });
      return;
    }

    const digitsOnly = rawValue.replace(/\D/g, '');
    onNumberChange(digitsOnly, {
      rawValue,
      isInternationalInput: false,
    });
  };

  // Keep raw digits while typing so caret/backspace behavior stays natural.
  // Apply libphonenumber formatting only when the field is not focused.
  const displayNumber = isFocused
    ? localNumber || ''
    : localNumber
      ? new AsYouType(country.code).input(localNumber)
      : '';

  return (
    <div className="pf-field">
      <label
        htmlFor={id}
        className={`pf-label ${isFilled(localNumber) ? 'pf-label-float' : ''}`}
      >
        {label}
      </label>
      <div
        className={[
          'pf-phone',
          disabled ? 'pf-input-disabled' : '',
          showError ? 'pf-phone-invalid' : '',
        ].join(' ')}
      >
        <div className="pf-phone-country">
          <Select
            inputId={id}
            isSearchable
            isDisabled={disabled}
            options={countryOptions}
            value={selectedOption}
            styles={countrySelectStyles}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            menuPlacement="auto"
            onChange={(opt) => opt && onCountryChange(opt.country)}
          />
        </div>
        <span className="pf-phone-divider" />
        <input
          id={`${id}-number`}
          className="pf-phone-input"
          type="tel"
          inputMode="numeric"
          autoComplete="tel-national"
          value={displayNumber}
          disabled={disabled}
          onChange={handleNumberChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => {
            setTouched(true);
            setIsFocused(false);
          }}
        />
      </div>
      {showError && (
        <div className="pf-field-error">Enter a valid number for {country.name}</div>
      )}
    </div>
  );
}

export default PhoneField;
